// Home.jsx
import { useMemo, useRef, useState } from "react";
import "./Home.css";

const INTRO_VIDEO_ID = "2b4L5B-E2cM";
const INITIAL_VISIBLE = 6;

// Works for Vite (VITE_*) and CRA (REACT_APP_*)
const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_CONTACT_API_URL) ||
  process.env.REACT_APP_CONTACT_API_URL ||
  "https://xjref277ki.execute-api.us-west-2.amazonaws.com";

// If your API expects a path like "/contact" or "/prod/contact", change it here:
const CONTACT_ENDPOINT = API_BASE;

const ServiceCard = ({ title, desc }) => (
  <div className="psoc-card">
    <h3 className="psoc-cardTitle">{title}</h3>
    <p className="psoc-cardText">{desc}</p>
  </div>
);

const VideoEmbed = ({ id, title }) => (
  <div className="psoc-video">
    <iframe
      src={`https://www.youtube.com/embed/${id}`}
      title={title}
      frameBorder="0"
      loading="lazy"
      referrerPolicy="strict-origin-when-cross-origin"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  </div>
);

const Home = () => {
  const [showAllVideos, setShowAllVideos] = useState(false);
  const [status, setStatus] = useState({ state: "idle", message: "" }); // idle | sending | success | error
  const [submitting, setSubmitting] = useState(false);

  const contactRef = useRef(null);
  const workRef = useRef(null);

  const videoIds = useMemo(
    () => [
      "AeTvUyrjSug", "6UqjRHTa5fc", "-VOM4PbtqDk", "e9hjRtHjOV4",
      "wXFl6xuVz6s", "9NeJU6s2Lx0", "4MzJzJ2zvuk", "-Pp-H6rl3u0",
      "bURe_ePMGH4", "Ye-e1qXK9Q4", "XGDdfMLjpDk", "OpRRRJEOoeQ",
      "Q8Dpob22UPc", "aUlKeZzSkv4", "HFHjQ1U9Hgc",
    ],
    []
  );

  const visibleVideos = showAllVideos ? videoIds : videoIds.slice(0, INITIAL_VISIBLE);

  const scrollToRef = (ref) => {
    if (!ref?.current) return;

    const NAV_OFFSET = 86; // tweak if your navbar height changes
    const y = ref.current.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;

    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const safeJson = async (res) => {
    const text = await res.text();
    try {
      return text ? JSON.parse(text) : {};
    } catch {
      return { message: text };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const form = e.currentTarget;

    const formData = {
      name: form.name.value.trim(),
      email: form.email.value.trim(),
      phone: form.phone.value.trim(),
      subject: form.subject.value.trim(),
      message: form.message.value.trim(),
    };

    setSubmitting(true);
    setStatus({ state: "sending", message: "Sending…" });

    try {
      const res = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await safeJson(res);

      if (!res.ok) {
        const msg = data?.message || "There was an issue submitting the form.";
        throw new Error(msg);
      }

      setStatus({ state: "success", message: data?.message || "Sent! We’ll get back to you soon." });
      form.reset();
    } catch (err) {
      console.error(err);
      setStatus({
        state: "error",
        message: err?.message || "There was an error submitting the form.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="psoc">

      {/* Hero */}
      <section className="psoc-hero">
        <div className="psoc-container psoc-heroGrid">
          <div className="psoc-heroText">
            <p className="psoc-badge">Colorado • Video • Audio • Drone</p>
            <h1 className="psoc-h1">Production Studio of Colorado LLC</h1>
            <p className="psoc-lead">
              Born and raised locally, we aim to provide quality audio and video to bring your vision to life.
            </p>

            <div className="psoc-ctaRow">
              <button className="psoc-btn psoc-btnPrimary" type="button" onClick={() => scrollToRef(contactRef)}>
                Get a Quote
              </button>
              <button className="psoc-btn psoc-btnSecondary" type="button" onClick={() => scrollToRef(workRef)}>
                Watch Our Work
              </button>
            </div>

            <div className="psoc-trust">
              <span className="psoc-pill">Fast turnaround</span>
              <span className="psoc-pill">High fidelity</span>
              <span className="psoc-pill">Clear communication</span>
            </div>
          </div>

          <div className="psoc-heroVideo" aria-label="Intro video">
            <VideoEmbed id={INTRO_VIDEO_ID} title="Production Studio of Colorado - Intro Video" />
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="psoc-section">
        <div className="psoc-container">
          <div className="psoc-sectionHead">
            <h2 className="psoc-h2">Services</h2>
            <p className="psoc-sub">
              Simple options that cover most projects. If you’re not sure what you need, send a message and we’ll guide you.
            </p>
          </div>

          <div className="psoc-grid3">
            <ServiceCard
              title="Video Production"
              desc="Custom videos for businesses, social media, weddings, and more."
            />
            <ServiceCard
              title="Audio Recording"
              desc="Voiceovers, music, radio commercials, and studio-quality recording services."
            />
            <ServiceCard
              title="Real Estate Videos"
              desc="Showcasing homes in the highest fidelity. Drone services available."
            />
          </div>
        </div>
      </section>

      {/* Portfolio */}
      <section className="psoc-section psoc-sectionAlt" ref={workRef} id="work">
        <div className="psoc-container">
          <div className="psoc-sectionHead psoc-sectionHeadRow">
            <div>
              <h2 className="psoc-h2">Our Work</h2>
              <p className="psoc-sub">A few recent projects. Click play and take a look.</p>
            </div>

            <div className="psoc-inlineActions">
              <button
                className="psoc-btn psoc-btnGhost"
                type="button"
                onClick={() => setShowAllVideos((v) => !v)}
              >
                {showAllVideos ? "Show Less" : "Load More"}
              </button>
            </div>
          </div>

          <div className="psoc-videoGrid">
            {visibleVideos.map((id) => (
              <VideoEmbed key={id} id={id} title={`Production Studio Video ${id}`} />
            ))}
          </div>

          {!showAllVideos && videoIds.length > INITIAL_VISIBLE && (
            <div className="psoc-center">
              <button className="psoc-btn psoc-btnSecondary" type="button" onClick={() => setShowAllVideos(true)}>
                Load More Videos
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Contact */}
      <section className="psoc-section" ref={contactRef} id="contact">
        <div className="psoc-container">
          <div className="psoc-sectionHead">
            <h2 className="psoc-h2">Contact Us</h2>
            <p className="psoc-sub">
              Tell us what you’re working on. Goals, style, timeline, and any links or references, and we’ll respond by email.
            </p>
          </div>

          <div className="psoc-contactCard">
            <form onSubmit={handleSubmit} className="psoc-form">
              <div className="psoc-formGrid">
                <div className="psoc-field">
                  <label className="psoc-label" htmlFor="name">Name</label>
                  <input id="name" name="name" type="text" placeholder="Name" required autoComplete="name" />
                </div>

                <div className="psoc-field">
                  <label className="psoc-label" htmlFor="email">Email</label>
                  <input id="email" name="email" type="email" placeholder="Email" required autoComplete="email" />
                </div>

                <div className="psoc-field">
                  <label className="psoc-label" htmlFor="phone">Phone</label>
                  <input id="phone" name="phone" type="tel" placeholder="Phone (optional)" autoComplete="tel" />
                </div>

                <div className="psoc-field">
                  <label className="psoc-label" htmlFor="subject">Subject</label>
                  <input id="subject" name="subject" type="text" placeholder="Subject (optional)" />
                </div>

                <div className="psoc-field psoc-fieldFull">
                  <label className="psoc-label" htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Type your message here..."
                    required
                    rows={7}
                  />
                </div>
              </div>

              <div className="psoc-formFooter">
                <button className="psoc-btn psoc-btnPrimary" type="submit" disabled={submitting}>
                  {submitting ? "Sending…" : "Submit"}
                </button>

                <div className="psoc-status" aria-live="polite">
                  {status.state !== "idle" && (
                    <span className={`psoc-statusMsg psoc-${status.state}`}>{status.message}</span>
                  )}
                </div>
              </div>
            </form>

            <p className="psoc-emailNote">
              Or email us directly at{" "}
              <a className="psoc-link" href="mailto:productionstudioofcolorado@gmail.com">
                productionstudioofcolorado@gmail.com
              </a>
            </p>
          </div>
        </div>
      </section>

      <footer className="psoc-footer">
        <div className="psoc-container psoc-footerInner">
          <span>© {new Date().getFullYear()} Production Studio of Colorado LLC</span>
          <button className="psoc-btn psoc-btnGhost" type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            Back to top
          </button>
        </div>
      </footer>
    </div>
  );
};

export default Home;
